<template>
  <publisher-dashboard-section-wrapper title="Overall Installation">
    <div class="d-flex justify-space-between align-center mb-4">
      <div class="period-selector">
        <v-select
          v-model="selectedPeriod"
          :items="periodOptions"
          label="Time Period"
          dense
          outlined
          hide-details
          style="max-width: 150px;"
          @change="onPeriodChange"
          :disabled="hasError"
        ></v-select>
      </div>
      <div v-if="totalInstalls !== null && !hasError" class="total-installs">
        <span class="text-caption text-muted">Total Installs:</span>
        <span class="text-h6 font-weight-bold ml-2">{{ totalInstalls }}</span>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="text-center pa-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-caption text-muted">Loading installation data...</div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="hasError" class="text-center pa-6">
      <v-icon color="error" size="48" class="mb-3">mdi-chart-line</v-icon>
      <div class="text-h6 mb-2">Unable to load installation data</div>
      <div class="text-body-2 text-muted mb-4">{{ errorMessage }}</div>
      <v-btn 
        color="primary" 
        outlined 
        @click="retryFetch"
        :loading="loading"
      >
        <v-icon left>mdi-refresh</v-icon>
        Try Again
      </v-btn>
    </div>
    
    <!-- Chart State -->
    <div v-else>
      <canvas id="overall-installation" height="350"></canvas>
    </div>
  </publisher-dashboard-section-wrapper>
</template>

<script>
import Chart from 'chart.js'
import PublisherDashboardSectionWrapper from './PublisherDashboardSectionWrapper.vue'
import { mapState } from 'vuex'
import marketplaceAPI from '@/services/marketplace'

export default {
  name: 'PublisherOverallInstallation',
  components: {
    PublisherDashboardSectionWrapper,
  },
  data() {
    return {
      loading: true,
      hasError: false,
      errorMessage: '',
      chart: null,
      isUpdating: false, // Flag to prevent concurrent updates
      selectedPeriod: 'monthly',
      totalInstalls: null,
      periodOptions: [
        { text: 'Daily', value: 'daily' },
        { text: 'Weekly', value: 'weekly' },
        { text: 'Monthly', value: 'monthly' },
        { text: 'Yearly', value: 'yearly' },
      ],
      chartData: {
        type: 'line',
        data: {
          labels: [],
          datasets: [
            {
              label: 'Plugin Installs',
              data: [],
              borderColor: '#36495d',
              backgroundColor: 'rgba(54, 73, 93, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          lineTension: 0.4,
          pointHoverRadius: 8,
          pointRadius: 4,
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  padding: 25,
                  stepSize: 1,
                },
                gridLines: {
                  color: 'rgba(0,0,0,0.1)',
                },
              },
            ],
            xAxes: [
              {
                gridLines: {
                  color: 'rgba(0,0,0,0.1)',
                },
              },
            ],
          },
          legend: {
            display: true,
            position: 'top',
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title: function(tooltipItems) {
                return tooltipItems[0].xLabel
              },
              label: function(tooltipItem) {
                return `Installs: ${tooltipItem.yLabel}`
              }
            }
          },
        },
      },
    }
  },
  computed: {
    ...mapState({
      currentPublisherInfo: ({ publisher_infos }) => publisher_infos.current,
    }),
  },
  watch: {
    currentPublisherInfo: {
      handler(newVal) {
        if (newVal?.parse_server_site_id) {
          this.fetchInstallsData()
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Fetch data if publisher info is available
    if (this.currentPublisherInfo?.parse_server_site_id) {
      this.fetchInstallsData()
    }
  },
  methods: {
    async fetchInstallsData() {
      // Prevent concurrent updates
      if (this.isUpdating) {
        return
      }
      
      try {
        this.isUpdating = true
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        
        if (!this.currentPublisherInfo?.parse_server_site_id) {
          this.showError('No publisher information available')
          return
        }

        // Map period to marketplace API time_period
        const timePeriodMap = {
          daily: 'day',
          weekly: 'week',
          monthly: 'month',
          yearly: 'year',
        }
        const timePeriod = timePeriodMap[this.selectedPeriod] || 'month'
        
        // Calculate date range based on period
        const endDate = new Date().toISOString()
        const startDate = this.getStartDateForPeriod()
        
        const analytics = await marketplaceAPI.getInstallationAnalytics({
          time_period: timePeriod,
          start_date: startDate,
          end_date: endDate,
        })
        
        // Handle analytics response format (could be wrapped in analytics property)
        const analyticsData = analytics.analytics || analytics
        
        if (analyticsData && analyticsData.by_time_period) {
          // Map marketplace analytics to chart format
          const chartData = {
            data: analyticsData.by_time_period.map((item) => ({
              label: item.period_label || item.period,
              count: item.installations || item.install_count || item.count || 0,
            })),
            totalInstalls: analyticsData.active_installations || analyticsData.total_installations || 0,
          }
          this.updateChartData(chartData)
        } else {
          this.showError('No installation data available')
        }
      } catch (error) {
        console.error('Error fetching installs data:', error)
        this.handleFetchError(error)
      } finally {
        this.loading = false
        this.isUpdating = false
      }
    },
    
    getStartDateForPeriod() {
      const now = new Date()
      switch (this.selectedPeriod) {
        case 'daily':
          now.setDate(now.getDate() - 30) // Last 30 days
          break
        case 'weekly':
          now.setDate(now.getDate() - 84) // Last 12 weeks
          break
        case 'monthly':
          now.setMonth(now.getMonth() - 12) // Last 12 months
          break
        case 'yearly':
          now.setFullYear(now.getFullYear() - 5) // Last 5 years
          break
      }
      return now.toISOString()
    },
    
    handleFetchError(error) {
      let errorMessage = 'An unexpected error occurred while loading installation data'
      
      if (error.code === 141) {
        errorMessage = 'The installation data function is not available on the server'
      } else if (error.code === 1) {
        errorMessage = 'Unable to connect to the data server'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      this.showError(errorMessage)
    },
    
    showError(message) {
      this.hasError = true
      this.errorMessage = message
      this.totalInstalls = null
      
      // Clear any existing chart
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },
    
    async retryFetch() {
      await this.fetchInstallsData()
    },
    
    getLimitForPeriod() {
      switch (this.selectedPeriod) {
        case 'daily':
          return 30 // Last 30 days
        case 'weekly':
          return 12 // Last 12 weeks
        case 'monthly':
          return 12 // Last 12 months
        case 'yearly':
          return 5 // Last 5 years
        default:
          return 12
      }
    },
    
    async onPeriodChange() {
      // Reset error state when changing periods
      this.hasError = false
      this.errorMessage = ''
      await this.fetchInstallsData()
    },
    
    updateChartData(installsData) {
      const labels = installsData.data?.map(item => item.label) || []
      const data = installsData.data?.map(item => item.count) || []
      this.totalInstalls = installsData.totalInstalls || 0
      
      // Destroy existing chart first
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
      
      // Create a fresh chart data object to avoid reference issues
      const newChartData = {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Plugin Installs',
              data: data,
              borderColor: '#36495d',
              backgroundColor: 'rgba(54, 73, 93, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          lineTension: 0.4,
          pointHoverRadius: 8,
          pointRadius: 4,
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  padding: 25,
                  stepSize: 1,
                },
                gridLines: {
                  color: 'rgba(0,0,0,0.1)',
                },
              },
            ],
            xAxes: [
              {
                gridLines: {
                  color: 'rgba(0,0,0,0.1)',
                },
              },
            ],
          },
          legend: {
            display: true,
            position: 'top',
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title: function(tooltipItems) {
                return tooltipItems[0].xLabel
              },
              label: function(tooltipItem) {
                return `Installs: ${tooltipItem.yLabel}`
              }
            }
          },
        },
      }
      
      // Create new chart in next tick to ensure DOM is ready
      this.$nextTick(() => {
        const ctx = document.getElementById('overall-installation')
        if (ctx) {
          try {
            this.chart = new Chart(ctx, newChartData)
          } catch (error) {
            console.error('Error creating chart:', error)
            this.showError('Failed to display chart')
          }
        } else {
          console.error('Canvas element not found - DOM may not be ready')
          // Retry once after a short delay
          setTimeout(() => {
            const retryCtx = document.getElementById('overall-installation')
            if (retryCtx) {
              try {
                this.chart = new Chart(retryCtx, newChartData)
              } catch (error) {
                console.error('Error creating chart on retry:', error)
                this.showError('Failed to display chart')
              }
            } else {
              console.error('Canvas element still not found on retry')
              this.showError('Chart container not available')
            }
          }, 100)
        }
      })
    },
  },
}
</script>

<style scoped>
.period-selector {
  min-width: 150px;
}

.total-installs {
  display: flex;
  align-items: center;
}
</style>
